/**
 * DSonic Studio — Patch Matrix
 *
 * Owns state and audio; the components own pixels. Twelve voices, each a row
 * of parameters on the left and a sequencer lane on the right, driven by one
 * shared clock through one per-voice gain stage into the master bus.
 */

import { getAudioContext } from '@strudel/webaudio';
import * as DSonic from '../../../dist/dsonic.js';

import { PatchMatrix } from './components/PatchMatrix.js';
import { StudioTransport } from './components/StudioTransport.js';
import { VoiceEditor } from './components/VoiceEditor.js';
import { SequencerClock } from './components/SequencerClock.js';
import {
  ENGINE_TYPES,
  ENGINE_SOUND_NAMES,
  ENGINE_PRIMARY_PARAMS,
  ENGINE_DISPLAY_NAMES,
  VOICE_HUES,
  getAllDefaults,
  getPrimaryDefaults,
  randomizeEngine,
  toEngineValue,
  toUiValue,
} from './engine-data.js';
import {
  FACTORY_PRESET_NAMES,
  isFactoryPreset,
  getFactoryPreset,
  getPresetsByCategory,
  DEFAULT_MIXER_LEVELS,
} from './presets.js';

const MAX_STEPS = 32;
const PRESETS_STORAGE_KEY = 'dsonic_studio_presets';

/** Gain multiplier per velocity level [off, soft, medium, full]. */
const VELOCITY_SCALES = [0, 0.4, 0.7, 1.0];
const ACCENT_BOOST = 1.3;

/** Undo depth. Deep enough to unwind a session, shallow enough to stay cheap. */
const UNDO_LIMIT = 60;

/* ------------------------------------------------------------------ *
 * State
 * ------------------------------------------------------------------ */

const studioState = {
  _sequencer: {
    stepCount: 16,
    bpm: 120,
    swing: 0,
    lanes: {},
    muted: {},
    soloed: {},
  },
  _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
  _master: 0.8,
};

function emptyLane() {
  return Array.from({ length: MAX_STEPS }, () => ({ velocity: 0, accent: false }));
}

ENGINE_TYPES.forEach((engineType) => {
  studioState[engineType] = getAllDefaults(engineType);
  studioState._sequencer.lanes[engineType] = emptyLane();
  studioState._sequencer.muted[engineType] = false;
  studioState._sequencer.soloed[engineType] = false;
  if (studioState._mixerLevels[engineType] === undefined) {
    studioState._mixerLevels[engineType] = 0.8;
  }
});

/* ------------------------------------------------------------------ *
 * Runtime handles
 * ------------------------------------------------------------------ */

let matrix = null;
let transport = null;
let clock = null;
let voiceEditor = null;

let masterGainNode = null;
let masterAnalyser = null;
let analyserBuffer = null;
const voiceGainNodes = {};

/** Per-voice meter envelope, driven by triggers and decayed each frame. */
const meterLevels = {};
let meterFrame = null;

let currentPresetName = null;
let viewMode = 'both';
const undoStack = [];

/* ------------------------------------------------------------------ *
 * Undo
 * ------------------------------------------------------------------ */

function snapshot() {
  return JSON.stringify(studioState);
}

/** Record the state a gesture is about to change. Coalesced by the caller. */
function pushUndo() {
  undoStack.push(snapshot());
  if (undoStack.length > UNDO_LIMIT) undoStack.shift();
}

function undo() {
  const previous = undoStack.pop();
  if (!previous) {
    showHint('Nothing to undo');
    return;
  }

  const restored = JSON.parse(previous);
  Object.keys(studioState).forEach((key) => delete studioState[key]);
  Object.assign(studioState, restored);

  applyStateToAudio();
  syncAllUi();
  showHint('Undo');
}

/* ------------------------------------------------------------------ *
 * Audio
 * ------------------------------------------------------------------ */

function setupAudioRouting() {
  const ctx = getAudioContext();

  masterGainNode = ctx.createGain();
  masterGainNode.gain.value = studioState._master;

  masterAnalyser = ctx.createAnalyser();
  masterAnalyser.fftSize = 1024;
  analyserBuffer = new Uint8Array(masterAnalyser.fftSize);

  masterGainNode.connect(masterAnalyser);
  masterGainNode.connect(ctx.destination);

  ENGINE_TYPES.forEach((engineType) => {
    const gain = ctx.createGain();
    gain.gain.value = studioState._mixerLevels[engineType] ?? 0.8;
    gain.connect(masterGainNode);
    voiceGainNodes[engineType] = gain;
    meterLevels[engineType] = 0;
  });
}

/** Push every level in state onto its gain node. */
function applyStateToAudio() {
  if (masterGainNode) masterGainNode.gain.value = studioState._master;
  ENGINE_TYPES.forEach((engineType) => {
    const gain = voiceGainNodes[engineType];
    if (gain) gain.gain.value = studioState._mixerLevels[engineType] ?? 0.8;
  });
  if (clock) {
    clock.setBpm(studioState._sequencer.bpm);
    clock.setSwing(studioState._sequencer.swing);
    clock.setStepCount(studioState._sequencer.stepCount);
  }
}

/** True when this voice should not sound right now (mute, or another solo). */
function isSilenced(engineType) {
  const { muted, soloed } = studioState._sequencer;
  if (muted[engineType]) return true;
  const anySoloed = ENGINE_TYPES.some((type) => soloed[type]);
  return anySoloed && !soloed[engineType];
}

function triggerSound(engineType, { velocity = 3, accent = false, time = null } = {}) {
  if (velocity === 0) return;

  const ctx = getAudioContext();
  if (ctx.state === 'suspended') ctx.resume();

  // Engine enums live as strings on the engine side and numbers in the UI.
  const params = {};
  Object.entries(studioState[engineType] || {}).forEach(([name, value]) => {
    params[name] = toEngineValue(engineType, name, value);
  });

  // Closed and open hat are one engine in two modes.
  let actualEngineType = engineType;
  if (engineType === 'closed_hat') {
    actualEngineType = 'hat';
    params.mode = 'closed';
  } else if (engineType === 'open_hat') {
    actualEngineType = 'hat';
    params.mode = 'open';
  }

  const safeVelocity = Math.max(0, Math.min(3, Math.floor(velocity) || 3));
  const gainValue = VELOCITY_SCALES[safeVelocity] * (accent ? ACCENT_BOOST : 1);
  const triggerTime = time !== null ? time : ctx.currentTime;

  try {
    const engine = DSonic.createEngine(ctx, actualEngineType, params);
    engine.trigger(triggerTime);

    const velocityGain = ctx.createGain();
    velocityGain.gain.value = gainValue;
    engine.getOutput().connect(velocityGain);
    velocityGain.connect(voiceGainNodes[engineType] || masterGainNode || ctx.destination);

    // Light the row's meter. Scheduled hits land in the future, so the flash
    // waits for the audio clock rather than firing when the note is queued.
    const level = Math.min(1, gainValue * (studioState._mixerLevels[engineType] ?? 0.8));
    const lead = Math.max(0, (triggerTime - ctx.currentTime) * 1000);
    setTimeout(() => {
      meterLevels[engineType] = Math.max(meterLevels[engineType] || 0, level);
    }, lead);

    const decay = params.decay || params.overallDecay || 0.5;
    setTimeout(() => engine.dispose(), (decay + 0.5) * 1000);
  } catch (err) {
    console.error(`Error triggering ${engineType}:`, err);
  }
}

/** Audition from the UI, honouring mute/solo so what you hear is what plays. */
function auditionVoice(engineType) {
  if (isSilenced(engineType)) return;
  triggerSound(engineType, { velocity: 3 });
  // Wake the animation loop so the row's meter shows the hit even when the
  // sequencer is stopped.
  startVisualSync();
}

/* ------------------------------------------------------------------ *
 * Clock
 * ------------------------------------------------------------------ */

function initClock() {
  if (clock) return;
  try {
    clock = new SequencerClock({
      audioContext: getAudioContext(),
      onStep: (stepIndex, time) => handleClockStep(stepIndex, time),
    });
    clock.setBpm(studioState._sequencer.bpm);
    clock.setSwing(studioState._sequencer.swing);
    clock.setStepCount(studioState._sequencer.stepCount);
  } catch (err) {
    console.error('Error initializing sequencer clock:', err);
  }
}

function handleClockStep(stepIndex, time) {
  ENGINE_TYPES.forEach((engineType) => {
    if (isSilenced(engineType)) return;
    const step = studioState._sequencer.lanes[engineType][stepIndex];
    if (step && step.velocity > 0) {
      triggerSound(engineType, { velocity: step.velocity, accent: step.accent, time });
    }
  });
}

function play() {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') ctx.resume();

  initClock();
  if (!clock) return;

  clock.start();
  transport.setPlaying(true);
  document.querySelector('.studio-container')?.setAttribute('data-playing', 'true');
  startVisualSync();
}

function stop() {
  if (clock) clock.stop();
  transport.setPlaying(false);
  document.querySelector('.studio-container')?.setAttribute('data-playing', 'false');
  matrix.setCurrentStep(-1);
}

function togglePlay() {
  if (clock && clock.getIsPlaying()) stop();
  else play();
}

/* ------------------------------------------------------------------ *
 * Animation — playhead, position readout, meters
 * ------------------------------------------------------------------ */

function startVisualSync() {
  if (meterFrame) return;

  const frame = () => {
    const playing = clock && clock.getIsPlaying();

    if (playing) {
      const step = clock.getCurrentStep();
      matrix.setCurrentStep(step);
      transport.setPosition(step);
    }

    // Voice meters: a fast attack from triggers, a slow visual release.
    ENGINE_TYPES.forEach((engineType) => {
      const level = (meterLevels[engineType] || 0) * 0.9;
      meterLevels[engineType] = level < 0.01 ? 0 : level;
      matrix.setMeterLevel(engineType, meterLevels[engineType]);
    });

    if (masterAnalyser && analyserBuffer) {
      masterAnalyser.getByteTimeDomainData(analyserBuffer);
      let peak = 0;
      for (let i = 0; i < analyserBuffer.length; i += 4) {
        const sample = Math.abs(analyserBuffer[i] - 128) / 128;
        if (sample > peak) peak = sample;
      }
      transport.setMeters(peak, peak * 0.92);
    }

    // Keep animating while anything is still lit, so a one-shot audition's
    // meter finishes its release instead of freezing mid-decay.
    const busy = playing || ENGINE_TYPES.some((type) => meterLevels[type] > 0);
    meterFrame = busy ? requestAnimationFrame(frame) : null;
  };

  meterFrame = requestAnimationFrame(frame);
}

/* ------------------------------------------------------------------ *
 * Edits
 * ------------------------------------------------------------------ */

function handleParamChange(engineType, paramName, value) {
  studioState[engineType][paramName] = value;
  matrix.syncParam(engineType, paramName);
  // Keep an open editor's knob in step with the matrix cell it mirrors.
  if (voiceEditor && voiceEditor.getEngineType() === engineType) {
    voiceEditor.syncParam(paramName);
  }
}

function handleStepChange(engineType, index, step) {
  if (index < 0 || index >= MAX_STEPS) return;
  studioState._sequencer.lanes[engineType][index] = {
    velocity: Math.max(0, Math.min(3, step.velocity | 0)),
    accent: !!step.accent,
  };
  matrix.syncStep(engineType, index);
}

function handleLevelChange(engineType, value) {
  const level = Math.min(1, Math.max(0, value));
  studioState._mixerLevels[engineType] = level;
  const gain = voiceGainNodes[engineType];
  if (gain) gain.gain.value = level;
  matrix.syncLevel(engineType);
}

function handleMuteChange(engineType, muted) {
  pushUndo();
  studioState._sequencer.muted[engineType] = muted;
  matrix.setMuted(engineType, muted);
  refreshSoloDimming();
}

function handleSoloChange(engineType, soloed) {
  pushUndo();
  studioState._sequencer.soloed[engineType] = soloed;
  matrix.setSoloed(engineType, soloed);
  refreshSoloDimming();
}

function refreshSoloDimming() {
  const { soloed } = studioState._sequencer;
  const anySoloed = ENGINE_TYPES.some((type) => soloed[type]);
  matrix.setSoloDimming(anySoloed, soloed);
}

function setStepCount(count) {
  if (![8, 16, 32].includes(count)) return;
  pushUndo();
  studioState._sequencer.stepCount = count;
  if (clock) clock.setStepCount(count);
  matrix.setStepCount(count);
  transport.setStepCount(count);
  updateStatusBar();
}

function setBpm(bpm) {
  studioState._sequencer.bpm = bpm;
  if (clock) clock.setBpm(bpm);
  transport.setBpm(bpm);
}

function setSwing(swing) {
  studioState._sequencer.swing = swing;
  if (clock) clock.setSwing(swing);
  transport.setSwing(swing);
}

function setMaster(value) {
  const level = Math.min(1, Math.max(0, value));
  studioState._master = level;
  if (masterGainNode) masterGainNode.gain.value = level;
  transport.setMaster(level);
}

function setView(mode) {
  viewMode = mode;
  document.querySelector('.studio-container')?.setAttribute('data-view', mode);
  transport.setView(mode);
}

/* ------------------------------------------------------------------ *
 * Pattern
 * ------------------------------------------------------------------ */

function clearPattern() {
  pushUndo();
  ENGINE_TYPES.forEach((engineType) => {
    studioState._sequencer.lanes[engineType] = emptyLane();
    matrix.syncSteps(engineType);
  });
  showHint('Pattern cleared');
}

/**
 * Seed a musical pattern rather than a uniform random one: each voice has a
 * density and a beat affinity, so kicks land on downbeats and snares on the
 * backbeat instead of scattering.
 */
function seedPattern() {
  pushUndo();

  const profiles = {
    analog_kick: { density: 0.25, downbeat: 0.8, upbeat: 0.05, offbeat: 0.1, velBias: 3 },
    membrane: { density: 0.1, downbeat: 0.4, upbeat: 0.2, offbeat: 0.15, velBias: 2 },
    hybrid_snare: { density: 0.2, downbeat: 0.05, upbeat: 0, offbeat: 0.1, velBias: 3, backbeat: true },
    clap: { density: 0.15, downbeat: 0.05, upbeat: 0, offbeat: 0.1, velBias: 3, backbeat: true },
    closed_hat: { density: 0.55, downbeat: 0.6, upbeat: 0.5, offbeat: 0.4, velBias: 2 },
    open_hat: { density: 0.12, downbeat: 0.1, upbeat: 0.3, offbeat: 0.15, velBias: 2 },
    cymbal: { density: 0.05, downbeat: 0.3, upbeat: 0, offbeat: 0, velBias: 3 },
    tom: { density: 0.08, downbeat: 0.1, upbeat: 0.2, offbeat: 0.2, velBias: 2 },
    rim: { density: 0.12, downbeat: 0.1, upbeat: 0.2, offbeat: 0.3, velBias: 2 },
    shaker: { density: 0.4, downbeat: 0.5, upbeat: 0.4, offbeat: 0.3, velBias: 1 },
    impact: { density: 0.04, downbeat: 0.5, upbeat: 0, offbeat: 0, velBias: 3 },
    fm: { density: 0.08, downbeat: 0.1, upbeat: 0.2, offbeat: 0.3, velBias: 2 },
  };

  const stepCount = studioState._sequencer.stepCount;

  ENGINE_TYPES.forEach((engineType) => {
    const steps = emptyLane();
    const p = profiles[engineType] || {
      density: 0.15,
      downbeat: 0.3,
      upbeat: 0.2,
      offbeat: 0.2,
      velBias: 2,
    };

    for (let i = 0; i < stepCount; i++) {
      const beatPos = i % 4;
      const beatIndex = Math.floor(i / 4);
      const isDownbeat = beatPos === 0;
      const isUpbeat = beatPos === 2;
      const isBackbeat = isDownbeat && beatIndex % 2 === 1;

      let probability;
      if (p.backbeat && isBackbeat) probability = 0.85;
      else if (isDownbeat) probability = p.density * (1 + p.downbeat);
      else if (isUpbeat) probability = p.density * (1 + p.upbeat);
      else probability = p.density * (1 + p.offbeat);

      if (Math.random() < probability) {
        const roll = Math.random();
        let velocity;
        if (p.velBias === 3) velocity = roll < 0.15 ? 1 : roll < 0.4 ? 2 : 3;
        else if (p.velBias === 1) velocity = roll < 0.5 ? 1 : roll < 0.85 ? 2 : 3;
        else velocity = roll < 0.25 ? 1 : roll < 0.7 ? 2 : 3;

        steps[i] = { velocity, accent: velocity === 3 && Math.random() < 0.3 };
      }
    }

    studioState._sequencer.lanes[engineType] = steps;
    matrix.syncSteps(engineType);
  });

  showHint('Pattern seeded');
}

/* ------------------------------------------------------------------ *
 * Patch actions
 * ------------------------------------------------------------------ */

function randomizeAll() {
  pushUndo();

  ENGINE_TYPES.forEach((engineType) => {
    studioState[engineType] = randomizeEngine(engineType);
    // Reset levels alongside, so a random patch arrives at a usable balance
    // instead of inheriting whatever the last one needed.
    handleLevelChange(engineType, DEFAULT_MIXER_LEVELS[engineType] ?? 0.8);
    matrix.syncParams(engineType);
  });

  voiceEditor?.sync();
  showHint('Randomized');
}

function resetAll() {
  pushUndo();

  ENGINE_TYPES.forEach((engineType) => {
    studioState[engineType] = getAllDefaults(engineType);
    matrix.syncParams(engineType);
  });

  voiceEditor?.sync();
  showHint('Reset to defaults');
}

/* ------------------------------------------------------------------ *
 * Presets
 * ------------------------------------------------------------------ */

function getSavedPresets() {
  try {
    const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (err) {
    console.error('Error loading presets:', err);
    return {};
  }
}

function savePresetsToStorage(presets) {
  try {
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(presets));
  } catch (err) {
    console.error('Error saving presets:', err);
  }
}

function savePreset(name) {
  if (!name || !name.trim()) return false;
  const trimmed = name.trim();

  if (isFactoryPreset(trimmed)) {
    showHint(`Cannot overwrite factory preset: ${trimmed}`);
    return false;
  }

  const presets = getSavedPresets();
  presets[trimmed] = JSON.parse(snapshot());
  savePresetsToStorage(presets);
  updatePresetList();
  showHint(`Saved: ${trimmed}`);
  return true;
}

function loadPreset(name) {
  let preset = getFactoryPreset(name);
  const isFactory = !!preset;

  if (!preset) preset = getSavedPresets()[name];
  if (!preset) {
    showHint('Preset not found');
    return false;
  }

  pushUndo();

  ENGINE_TYPES.forEach((engineType) => {
    if (preset[engineType]) {
      Object.assign(studioState[engineType], preset[engineType]);
    }

    const level = preset._mixerLevels?.[engineType];
    if (level !== undefined) {
      studioState._mixerLevels[engineType] = level;
    }
  });

  if (preset._master !== undefined) studioState._master = preset._master;

  if (preset._sequencer) {
    const seq = preset._sequencer;
    if (seq.bpm !== undefined) studioState._sequencer.bpm = seq.bpm;
    if (seq.swing !== undefined) studioState._sequencer.swing = seq.swing;
    if (seq.stepCount !== undefined) studioState._sequencer.stepCount = seq.stepCount;

    ENGINE_TYPES.forEach((engineType) => {
      const lane = emptyLane();
      (seq.lanes?.[engineType] || []).forEach((step, i) => {
        if (i < MAX_STEPS && step) {
          lane[i] = { velocity: step.velocity | 0, accent: !!step.accent };
        }
      });
      studioState._sequencer.lanes[engineType] = lane;
      studioState._sequencer.muted[engineType] = !!seq.muted?.[engineType];
      studioState._sequencer.soloed[engineType] = !!seq.soloed?.[engineType];
    });
  }

  applyStateToAudio();
  syncAllUi();

  currentPresetName = name;
  const select = document.getElementById('preset-select');
  if (select) select.value = name;

  showHint(`${isFactory ? '⚡ ' : ''}Loaded: ${name}`);
  return true;
}

function deletePreset(name) {
  if (isFactoryPreset(name)) {
    showHint(`Cannot delete factory preset: ${name}`);
    return;
  }

  const presets = getSavedPresets();
  if (presets[name]) {
    delete presets[name];
    savePresetsToStorage(presets);
    updatePresetList();
    showHint(`Deleted: ${name}`);
  }
}

function updatePresetList() {
  const select = document.getElementById('preset-select');
  if (!select) return;

  while (select.options.length > 1) select.remove(1);

  const categories = getPresetsByCategory();
  ['Classic', 'Electronic', 'Experimental', 'Acoustic', 'Hip-Hop', 'Synthesis'].forEach(
    (category) => {
      if (!categories[category]) return;
      const group = document.createElement('optgroup');
      group.label = `⚡ ${category}`;
      categories[category].forEach(({ name }) => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        group.appendChild(option);
      });
      select.appendChild(group);
    }
  );

  const userPresets = Object.keys(getSavedPresets()).sort();
  if (userPresets.length > 0) {
    const group = document.createElement('optgroup');
    group.label = '💾 My Presets';
    userPresets.forEach((name) => {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = name;
      group.appendChild(option);
    });
    select.appendChild(group);
  }

  const deleteBtn = document.getElementById('delete-preset-btn');
  if (deleteBtn) {
    deleteBtn.style.display = userPresets.length > 0 ? 'flex' : 'none';
  }
}

/* ------------------------------------------------------------------ *
 * Code export
 * ------------------------------------------------------------------ */

/**
 * Steps → a mini-notation sequence, one token per step, plus a parallel gain
 * sequence of the same length so the two align event for event.
 *
 * The voice's channel level is folded into each step's gain rather than added
 * as a second `.gain()` call: Strudel's controls set rather than multiply, so
 * a trailing `.gain(level)` would discard the per-step velocities entirely.
 */
function laneToNotation(engineType, steps, stepCount, level) {
  const sound = ENGINE_SOUND_NAMES[engineType];
  const tokens = [];
  const gains = [];
  let hasHit = false;

  for (let i = 0; i < stepCount; i++) {
    const step = steps[i];
    if (!step || step.velocity <= 0) {
      tokens.push('~');
      gains.push('~');
      continue;
    }

    hasHit = true;
    tokens.push(sound);
    const gain = VELOCITY_SCALES[step.velocity] * (step.accent ? ACCENT_BOOST : 1) * level;
    gains.push(String(Number(gain.toFixed(3))));
  }

  return hasHit ? { pattern: tokens.join(' '), gains: gains.join(' ') } : null;
}

function generateStrudelCode() {
  const { stepCount, bpm } = studioState._sequencer;
  const lines = [];

  ENGINE_TYPES.forEach((engineType) => {
    if (studioState._sequencer.muted[engineType]) return;

    const notation = laneToNotation(
      engineType,
      studioState._sequencer.lanes[engineType],
      stepCount,
      studioState._mixerLevels[engineType] ?? 0.8
    );
    if (!notation) return;

    // Only parameters the user moved off the default are worth emitting.
    const defaults = getPrimaryDefaults(engineType);
    const tweaks = (ENGINE_PRIMARY_PARAMS[engineType] || [])
      .map((param) => {
        const ui = toUiValue(engineType, param.name, studioState[engineType][param.name]);
        const base = toUiValue(engineType, param.name, defaults[param.name]);
        if (typeof ui !== 'number' || Math.abs(ui - base) <= 0.001) return '';

        // Enum params go back out as the strings the engine expects.
        const out = toEngineValue(engineType, param.name, ui);
        const literal =
          typeof out === 'string'
            ? `"${out}"`
            : Number.isInteger(out)
              ? out
              : Number(out.toFixed(3));
        return `.${param.name}(${literal})`;
      })
      .join('');

    lines.push(`  s("${notation.pattern}")\n    .gain("${notation.gains}")${tweaks}`);
  });

  if (lines.length === 0) {
    return [
      '// DSonic Studio — no steps programmed yet.',
      '// Click cells in the sequencer, then copy again.',
      's("dsonic_akick ~ ~ ~ dsonic_akick ~ ~ ~")',
    ].join('\n');
  }

  // One cycle is one bar, so cycles-per-minute is bars-per-minute.
  return [
    '// DSonic Studio export',
    `setcpm(${(bpm / 4).toFixed(2).replace(/\.?0+$/, '')})`,
    '',
    'stack(',
    lines.join(',\n'),
    ')',
  ].join('\n');
}

async function copyCode() {
  const code = generateStrudelCode();
  try {
    await navigator.clipboard.writeText(code);
    showHint('Copied!');
  } catch (err) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showHint('Copied!');
    } catch (fallbackErr) {
      console.error('Failed to copy:', err, fallbackErr);
      showHint('Copy failed');
    }
  }
}

/* ------------------------------------------------------------------ *
 * Deep edit
 * ------------------------------------------------------------------ */

function openVoiceEditor(engineType) {
  if (voiceEditor && voiceEditor.getEngineType() === engineType) {
    closeVoiceEditor();
    return;
  }
  if (voiceEditor) closeVoiceEditor(true);

  const zone = document.querySelector('.matrix-zone');
  if (!zone) return;

  voiceEditor = new VoiceEditor({
    engineType,
    index: ENGINE_TYPES.indexOf(engineType),
    total: ENGINE_TYPES.length,
    getValue: (type, name) => studioState[type]?.[name],
    onChange: (type, name, value) => {
      studioState[type][name] = value;
      matrix.syncParam(type, name);
    },
    onClose: () => closeVoiceEditor(),
  });

  zone.appendChild(voiceEditor.getElement());
  voiceEditor.getElement().querySelector('.collapse-btn')?.focus();
}

function closeVoiceEditor(immediate = false) {
  if (!voiceEditor) return;
  const editor = voiceEditor;
  voiceEditor = null;

  if (immediate) {
    editor.destroy();
    return;
  }

  editor.getElement().style.animation = 'expandOut 0.1s ease-out forwards';
  setTimeout(() => editor.destroy(), 90);
}

/* ------------------------------------------------------------------ *
 * UI sync
 * ------------------------------------------------------------------ */

function syncAllUi() {
  matrix.setStepCount(studioState._sequencer.stepCount);
  matrix.syncAll();

  ENGINE_TYPES.forEach((engineType) => {
    matrix.setMuted(engineType, studioState._sequencer.muted[engineType]);
    matrix.setSoloed(engineType, studioState._sequencer.soloed[engineType]);
  });
  refreshSoloDimming();

  transport.setBpm(studioState._sequencer.bpm);
  transport.setSwing(studioState._sequencer.swing);
  transport.setStepCount(studioState._sequencer.stepCount);
  transport.setMaster(studioState._master);

  voiceEditor?.sync();
  updateStatusBar();
}

function updateStatusBar() {
  const steps = document.getElementById('status-steps');
  if (steps) steps.textContent = `${studioState._sequencer.stepCount} STEPS`;

  const params = document.getElementById('status-params');
  if (params) {
    const count = ENGINE_TYPES.reduce(
      (total, type) => total + (ENGINE_PRIMARY_PARAMS[type]?.length || 0),
      0
    );
    params.textContent = `${count} PARAMS`;
  }

  const voices = document.getElementById('status-voices');
  if (voices) voices.textContent = `${ENGINE_TYPES.length} VOICES`;
}

function showHint(message) {
  const hint = document.getElementById('export-hint');
  if (!hint) return;
  hint.textContent = message;
  hint.classList.add('show');
  clearTimeout(showHint.timer);
  showHint.timer = setTimeout(() => hint.classList.remove('show'), 2000);
}

/* ------------------------------------------------------------------ *
 * Wiring
 * ------------------------------------------------------------------ */

function createMatrix() {
  const scroll = document.querySelector('.matrix-scroll');
  if (!scroll) return;

  matrix = new PatchMatrix({
    engineTypes: ENGINE_TYPES,
    stepCount: studioState._sequencer.stepCount,
    // State holds engine-facing values; a few params are string enums there
    // and numeric indices in the UI, so convert on the way in.
    getParamValue: (engineType, name) =>
      toUiValue(engineType, name, studioState[engineType]?.[name]),
    getStep: (engineType, index) => studioState._sequencer.lanes[engineType]?.[index],
    getLevel: (engineType) => studioState._mixerLevels[engineType] ?? 0.8,
    onParamChange: handleParamChange,
    onStepChange: handleStepChange,
    onLevelChange: handleLevelChange,
    onMuteChange: handleMuteChange,
    onSoloChange: handleSoloChange,
    onAudition: auditionVoice,
    onExpand: openVoiceEditor,
    onGestureStart: () => pushUndo(),
  });

  scroll.append(matrix.getParamsPanel(), matrix.getStepsPanel());
  matrix.setFocus(ENGINE_TYPES[0]);
}

function createTransport() {
  const container = document.querySelector('.studio-container');
  const anchor = document.querySelector('.matrix-zone');
  if (!container || !anchor) return;

  transport = new StudioTransport({
    bpm: studioState._sequencer.bpm,
    swing: studioState._sequencer.swing,
    stepCount: studioState._sequencer.stepCount,
    view: viewMode,
    master: studioState._master,
    onPlay: () => togglePlay(),
    onStop: () => stop(),
    onBpmChange: setBpm,
    onSwingChange: setSwing,
    onStepCountChange: setStepCount,
    onSeedPattern: seedPattern,
    onClearPattern: clearPattern,
    onViewChange: setView,
    onMasterChange: setMaster,
  });

  container.insertBefore(transport.getElement(), anchor);
}

function setupToolbar() {
  document.getElementById('copy-btn')?.addEventListener('click', copyCode);
  document.getElementById('randomize-btn')?.addEventListener('click', randomizeAll);
  document.getElementById('reset-btn')?.addEventListener('click', resetAll);

  document.getElementById('preset-select')?.addEventListener('change', (e) => {
    if (e.target.value) loadPreset(e.target.value);
  });

  const nameInput = document.getElementById('preset-name');
  const commitSave = () => {
    if (savePreset(nameInput?.value)) nameInput.value = '';
  };
  document.getElementById('save-preset-btn')?.addEventListener('click', commitSave);
  nameInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') commitSave();
  });

  document.getElementById('delete-preset-btn')?.addEventListener('click', () => {
    const names = Object.keys(getSavedPresets());
    if (names.length === 0) {
      showHint('No presets to delete');
      return;
    }
    const name = prompt(`Enter preset name to delete:\n\nSaved presets: ${names.join(', ')}`);
    if (name && getSavedPresets()[name]) deletePreset(name);
    else if (name) showHint('Preset not found');
  });

  updatePresetList();
}

function setupKeyboard() {
  document.addEventListener('keydown', (e) => {
    const tag = e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      undo();
      return;
    }

    if (e.metaKey || e.ctrlKey || e.altKey) return;

    if (e.code === 'Space') {
      e.preventDefault();
      togglePlay();
      return;
    }

    if (e.key === 'Escape' && voiceEditor) {
      e.preventDefault();
      closeVoiceEditor();
      return;
    }

    // 1–9 jump to a voice and audition it, so the row you land on speaks.
    if (e.key >= '1' && e.key <= '9') {
      const engineType = ENGINE_TYPES[Number(e.key) - 1];
      if (engineType) {
        e.preventDefault();
        matrix.setFocus(engineType);
        auditionVoice(engineType);
        startVisualSync();
      }
    }
  });
}

/* ------------------------------------------------------------------ *
 * Boot
 * ------------------------------------------------------------------ */

async function initialize() {
  const container = document.querySelector('.studio-container');
  container?.setAttribute('data-view', viewMode);
  container?.setAttribute('data-playing', 'false');

  createMatrix();
  createTransport();
  setupAudioRouting();
  setupToolbar();
  setupKeyboard();

  // Start on a factory patch so the page has something to play immediately.
  const firstPreset = FACTORY_PRESET_NAMES[0];
  if (firstPreset) {
    loadPreset(firstPreset);
    undoStack.length = 0;
  } else {
    syncAllUi();
  }

  window.DSonic = DSonic;
  window.getAudioContext = getAudioContext;
  window.studioState = studioState;
  window.studioDebug = {
    voiceHues: VOICE_HUES,
    displayNames: ENGINE_DISPLAY_NAMES,
    generateStrudelCode,
    getPreset: () => currentPresetName,
    getView: () => viewMode,
  };
  window.isInitialized = true;
}

initialize().catch((err) => {
  console.error('DSonic Studio initialization error:', err);
  window.isInitialized = true;
});
